/**
 * Unified Scroll Manager
 *
 * Single source of truth for scroll state management across:
 * - Navigation autoscroll (navScrollTo)
 * - Accordion auto-scroll (accordionScrollTo)
 * - CSS scroll-snap coordination
 *
 * Features:
 * - Pending scroll registry prevents premature snap re-enable
 * - Automatic snap disable/enable based on active operations
 * - Coordinated timing with separate buffers for different use cases
 * - No inline style manipulation (uses data attributes)
 */

// ─────────────────────────────────────────────────────────────
// PENDING SCROLL REGISTRY
// ─────────────────────────────────────────────────────────────

const pendingScrolls = new Map()

/**
 * Register a pending scroll operation
 * @param {string} id - Unique identifier for this scroll operation
 * @param {number} duration - Duration of the scroll operation (ms)
 */
function registerPendingScroll(id, duration) {
  pendingScrolls.set(id, Date.now() + duration)
}

/**
 * Unregister a pending scroll operation
 * @param {string} id - Unique identifier for this scroll operation
 */
function unregisterPendingScroll(id) {
  pendingScrolls.delete(id)
}

/**
 * Check if any scroll operation is still pending
 * @returns {boolean} True if any scroll is still in progress
 */
function hasPendingScroll() {
  const now = Date.now()
  for (const [id, expiry] of pendingScrolls.entries()) {
    if (now >= expiry) {
      pendingScrolls.delete(id)
    }
  }
  return pendingScrolls.size > 0
}

// ─────────────────────────────────────────────────────────────
// SNAP STATE MANAGER
// ─────────────────────────────────────────────────────────────

/**
 * Disable scroll-snap globally
 * Uses data attribute instead of inline styles for cleaner CSS
 */
export function disableScrollSnap() {
  document.documentElement.setAttribute('data-scroll-snap-disabled', 'true')
}

/**
 * Enable scroll-snap globally
 * Only re-enables if no pending scroll operations remain
 */
export function enableScrollSnap() {
  if (!hasPendingScroll()) {
    document.documentElement.removeAttribute('data-scroll-snap-disabled')
  }
}

/**
 * Schedule snap re-enable after delay
 * Checks for pending scrolls before re-enabling
 * @param {number} delay - Delay before re-enabling (ms)
 */
export function scheduleSnapEnable(delay) {
  setTimeout(() => {
    enableScrollSnap()
  }, delay)
}

// ─────────────────────────────────────────────────────────────
// NAVIGATION AUTOSCROLL (700ms + 200ms buffer)
// ─────────────────────────────────────────────────────────────

/**
 * Navigation scroll with snap coordination
 * Duration: 700ms scroll + 200ms buffer = 900ms total
 *
 * @param {HTMLElement} element - Target element to scroll to
 * @param {Object} options - { block: 'start' | 'center', duration?: number }
 */
export function navScrollTo(element, options = {}) {
  if (!element) return

  const { block = 'start', duration = 700 } = options
  const scrollId = `nav-${Date.now()}`

  registerPendingScroll(scrollId, 900)

  disableScrollSnap()

  const targetPosition = block === 'center'
    ? element.getBoundingClientRect().top + window.pageYOffset - (window.innerHeight / 2) + (element.offsetHeight / 2)
    : element.getBoundingClientRect().top + window.pageYOffset - 20

  const startPosition = window.pageYOffset
  const distance = targetPosition - startPosition
  let startTime = null

  const easeInOutCubic = (t) => {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
  }

  const animation = (currentTime) => {
    if (startTime === null) startTime = currentTime
    const timeElapsed = currentTime - startTime
    const progress = Math.min(timeElapsed / duration, 1)

    window.scrollTo(0, startPosition + distance * easeInOutCubic(progress))

    if (progress < 1) {
      requestAnimationFrame(animation)
    } else {
      // Land exactly on target (avoid shortfall that hands snap to previous section)
      window.scrollTo(0, targetPosition)
      unregisterPendingScroll(scrollId)

      // Soft-snap sections (FAQ / Cases): clear full-disable only — proximity stays.
      // Elsewhere: re-enable mandatory after a short settle.
      const soft =
        document.documentElement.hasAttribute('data-faq-section-active') ||
        document.documentElement.hasAttribute('data-cases-section-active')
      if (soft) {
        document.documentElement.removeAttribute('data-scroll-snap-disabled')
      } else {
        scheduleSnapEnable(200)
      }
    }
  }

  requestAnimationFrame(animation)
}

// ─────────────────────────────────────────────────────────────
// ACCORDION AUTOSCROLL (1000ms stability + 300ms buffer)
// ─────────────────────────────────────────────────────────────

/**
 * Accordion scroll with snap coordination
 * Duration: 1000ms stability + 300ms buffer = 1300ms total
 *
 * @param {HTMLElement} element - Target element to scroll to
 * @param {number} buffer - Offset from top (px)
 */
export function accordionScrollTo(element, buffer = 100) {
  if (!element) return

  const scrollId = `accordion-${Date.now()}`

  registerPendingScroll(scrollId, 1300)

  disableScrollSnap()

  const rect = element.getBoundingClientRect()
  const scrollTop = window.scrollY + rect.top - buffer

  window.scrollTo({
    top: Math.max(0, scrollTop),
    behavior: 'smooth'
  })

  // Wait for the smooth scroll to actually finish before re-enabling snap,
  // otherwise proximity snap can yank the viewport mid-animation. scrollend
  // fires reliably in all modern browsers; 1000ms fallback for safety.
  const finish = () => {
    scheduleSnapEnable(0)
    unregisterPendingScroll(scrollId)
    window.removeEventListener('scrollend', finish)
    clearTimeout(fallback)
  }
  const fallback = setTimeout(finish, 1000)
  window.addEventListener('scrollend', finish, { once: true })
}

const easeInOutCubic = (t) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2

/**
 * Pin an element toward the top of the viewport.
 *
 * Used by FAQ after accordion expand. Keeps snap fully off during the pin,
 * then clears data-scroll-snap-disabled so soft-snap (e.g. data-faq-section-active
 * → proximity) applies — never schedules delayed mandatory re-enable (bounce).
 *
 * @param {HTMLElement} element
 * @param {{ offset?: number, duration?: number }} options
 *   offset — px from viewport top (default 80)
 *   duration — ms ease-in-out scroll (default 800; 0 = instant)
 */
export function pinElementToViewport(element, options = {}) {
  if (!element) return

  const { offset = 80, duration = 800 } = options
  const html = document.documentElement
  const scrollId = `pin-${Date.now()}`

  disableScrollSnap()
  registerPendingScroll(scrollId, Math.max(duration, 0) + 50)

  const prevBehavior = html.style.scrollBehavior
  html.style.scrollBehavior = 'auto'

  const startY = window.scrollY
  const targetY = Math.max(0, startY + element.getBoundingClientRect().top - offset)
  const distance = targetY - startY

  const finish = () => {
    html.style.scrollBehavior = prevBehavior
    unregisterPendingScroll(scrollId)
    // Soft-snap (proximity) or default mandatory — no forced mandatory timer
    document.documentElement.removeAttribute('data-scroll-snap-disabled')
  }

  if (duration <= 0 || Math.abs(distance) < 1) {
    window.scrollTo(0, targetY)
    finish()
    return
  }

  let startTime = null
  const animation = (now) => {
    if (startTime === null) startTime = now
    const t = Math.min((now - startTime) / duration, 1)
    window.scrollTo(0, startY + distance * easeInOutCubic(t))
    if (t < 1) {
      requestAnimationFrame(animation)
    } else {
      finish()
    }
  }
  requestAnimationFrame(animation)
}
